import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, FileSpreadsheet, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RequirePermission from '../../../../components/AccessControl/RequirePermission';
import { useStore } from '../../../../context/StoreContext';
import {
    loadServices, saveServicesBulk, servicesFetch, errorText, toNumber, clean, serviceKey, effectivePrice, newRowId,
    readColumn, discountTypeOf, isYes, readSheetFile, writeSheet, writeCsv,
} from '../../pricelistUtils';
import styles from './ServiceMaster.module.css';

// =============================================================================
// Add services (several rows, or an Excel / CSV) or edit one service (?serviceId=).
// ?category=&demographic= pre-fill new rows (from "Add service" on a category).
// =============================================================================

const makeRow = (s = {}, defaults = {}) => {
    const d = s.discount || {};
    return {
        id: s.id || s._id || newRowId('service'),
        name: s.name || '',
        demographic: s.targetDemographic ?? defaults.demographic ?? '',
        category: s.category ?? defaults.category ?? '',
        duration: s.duration ?? '', price: s.price ?? '',
        discountActive: Boolean(d.isActive), discountType: d.type || 'Percentage', discountValue: d.isActive ? (d.value ?? '') : '',
    };
};
const keyOfRow = (r) => serviceKey(r.demographic, r.category, r.name);

const rowsFromSheet = (sheet) => {
    const byKey = new Map();
    sheet.forEach((src) => {
        const name = clean(readColumn(src, ['service name', 'name', 'service']));
        if (!name) return;
        const discountValue = readColumn(src, ['discount value', 'discount amount', 'offer value', 'offer']);
        const active = isYes(readColumn(src, ['discount active', 'offer active'])) || String(discountValue).trim() !== '';
        const row = {
            id: newRowId('import'), name,
            demographic: clean(readColumn(src, ['audience', 'target demographic', 'demographic', 'gender', 'section', 'customer type'])),
            category: clean(readColumn(src, ['category', 'service category'])),
            duration: Math.max(0, toNumber(readColumn(src, ['duration', 'service duration', 'duration mins', 'minutes', 'mins']), 0)),
            price: Math.max(0, toNumber(readColumn(src, ['base price', 'price', 'mrp', 'rate', 'amount']), 0)),
            discountActive: active, discountType: discountTypeOf(readColumn(src, ['discount type', 'offer type'])),
            discountValue: active ? Math.max(0, toNumber(discountValue)) : '',
        };
        byKey.set(keyOfRow(row), row);   // same Audience + Category + Name: the last row wins
    });
    return [...byKey.values()];
};

const exportRows = (rows) => rows.filter((r) => clean(r.name)).map((r) => ({
    Audience: clean(r.demographic), Category: clean(r.category), 'Service Name': clean(r.name),
    'Duration (mins)': toNumber(r.duration), 'Base Price': toNumber(r.price),
    'Discount Type': r.discountActive ? r.discountType : '', 'Discount Value': r.discountActive ? toNumber(r.discountValue) : '',
    'Effective Price': effectivePrice(r),
}));

const toPayload = (r) => ({
    name: clean(r.name), targetDemographic: clean(r.demographic), category: clean(r.category),
    duration: Math.max(1, Math.round(toNumber(r.duration))), price: Math.max(0, toNumber(r.price)),
    discount: { isActive: Boolean(r.discountActive) && toNumber(r.discountValue) > 0, type: r.discountType, value: r.discountActive ? Math.max(0, toNumber(r.discountValue)) : 0 },
});

const ServiceMaster = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { activeStore } = useStore();
    const fileInputRef = useRef(null);
    const storeId = activeStore?.id || activeStore?._id;
    const serviceId = params.get('serviceId') || '';
    const defaults = useMemo(() => ({ category: params.get('category') || '', demographic: params.get('demographic') || '' }), [params]);
    const isEditing = Boolean(serviceId);

    const [services, setServices] = useState([]);
    const [rows, setRows] = useState([makeRow({}, defaults)]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [status, setStatus] = useState({ type: '', text: '', details: [] });

    const validRows = rows.filter((r) => clean(r.name));
    const audiences = useMemo(() => [...new Set(services.map((s) => s.targetDemographic).filter(Boolean))], [services]);
    const categories = useMemo(() => [...new Set(services.map((s) => s.category).filter(Boolean))], [services]);

    const fetchServices = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') { setIsLoading(false); return; }
        setIsLoading(true);
        try {
            const data = await loadServices(storeId);
            setServices(data);
            if (isEditing) {
                const s = data.find((x) => x.id === serviceId);
                setRows([makeRow(s || {})]);
                if (!s) setStatus({ type: 'error', text: 'This service could not be found. It may have been removed.', details: [] });
            } else {
                setRows([makeRow({}, defaults)]);
            }
        } catch (e) { setStatus({ type: 'error', text: errorText(e), details: [] }); } finally { setIsLoading(false); }
    }, [storeId, isEditing, serviceId, defaults]);
    useEffect(() => { fetchServices(); }, [fetchServices]);

    const setField = (id, field, value) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    const setOffer = (id, value) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, discountActive: value !== 'None', discountType: value === 'None' ? 'Percentage' : value, discountValue: value === 'None' ? '' : r.discountValue } : r)));
    const addRow = () => setRows((rs) => [...rs, makeRow({}, defaults)]);
    const removeRow = (id) => setRows((rs) => { const n = rs.filter((r) => r.id !== id); return n.length ? n : [makeRow({}, defaults)]; });

    const importFile = async (file) => {
        if (!file || isEditing) return;
        setIsImporting(true);
        try {
            const imported = rowsFromSheet(await readSheetFile(file));
            if (!imported.length) throw new Error('No services found. Use Audience, Category, Service Name, Duration and Base Price columns.');
            setRows(imported);
            const secs = [...new Set(imported.map((r) => r.demographic).filter(Boolean))];
            setStatus({ type: 'success', text: `${imported.length} services loaded${secs.length ? ` for ${secs.join(', ')}` : ''}. Check them, then Save.`, details: [] });
        } catch (e) { setStatus({ type: 'error', text: errorText(e), details: [] }); } finally { setIsImporting(false); }
    };

    const validate = () => {
        const problems = [];
        const seen = new Map();
        const existingKeys = new Map(services.filter((s) => s.id !== serviceId).map((s) => [serviceKey(s.targetDemographic, s.category, s.name), s]));
        validRows.forEach((r, i) => {
            const n = i + 1;
            if (!clean(r.demographic) || !clean(r.category)) problems.push(`Row ${n} (${r.name}): audience and category are needed.`);
            if (r.duration === '' || toNumber(r.duration) < 1) problems.push(`Row ${n} (${r.name}): duration must be at least 1 minute.`);
            if (r.price === '') problems.push(`Row ${n} (${r.name}): base price is missing.`);
            if (r.discountActive && r.discountType === 'Percentage' && toNumber(r.discountValue) > 100) problems.push(`Row ${n} (${r.name}): % offer cannot be more than 100.`);
            const k = keyOfRow(r);
            if (seen.has(k)) problems.push(`Row ${n}: same as row ${seen.get(k)}.`);
            seen.set(k, n);
            if (isEditing && existingKeys.has(k)) problems.push(`"${r.name}" already exists in ${r.demographic} › ${r.category}.`);
        });
        if (problems.length) { setStatus({ type: 'error', text: 'Fix these before saving:', details: problems.slice(0, 10) }); return false; }
        return true;
    };

    const save = async () => {
        if (!storeId || !validRows.length || !validate()) return;
        setIsSaving(true);
        setStatus({ type: '', text: '', details: [] });
        try {
            if (isEditing) {
                const json = await servicesFetch(`/${serviceId}`, { method: 'PUT', storeId, body: toPayload(validRows[0]) });
                if (!json.success) throw new Error(json.message);
                setStatus({ type: 'success', text: json.message || 'Service saved.', details: [] });
                await fetchServices();
            } else {
                // already-existing services in the rows are updated, not duplicated
                const json = await saveServicesBulk(storeId, validRows.map((r, i) => ({ rowNo: i + 1, ...toPayload(r) })));
                const failed = (json.data?.results || []).filter((x) => x.status === 'failed');
                setStatus({
                    type: failed.length ? (json.success ? 'warn' : 'error') : 'success',
                    text: json.message,
                    details: failed.map((f) => `Row ${f.rowNo} (${f.name}): ${f.message}`).slice(0, 10),
                });
                if (json.success && !failed.length) setRows([makeRow({}, defaults)]);
                if (json.success) setServices(await loadServices(storeId));
            }
        } catch (e) { setStatus({ type: 'error', text: errorText(e), details: [] }); } finally { setIsSaving(false); }
    };

    const template = () => writeSheet([
        { Audience: 'Men', Category: 'Hair', 'Service Name': 'Haircut', 'Duration (mins)': 30, 'Base Price': 300, 'Discount Type': '', 'Discount Value': '' },
        { Audience: 'Women', Category: 'Hair', 'Service Name': 'Haircut', 'Duration (mins)': 45, 'Base Price': 600, 'Discount Type': 'Percentage', 'Discount Value': 10 },
        { Audience: 'Kids', Category: 'Hair', 'Service Name': 'Haircut', 'Duration (mins)': 20, 'Base Price': 200, 'Discount Type': '', 'Discount Value': '' },
    ], 'Service_Import_Template.xlsx', 'Services');

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.pageHeader}>
                <div className={styles.headerIdentity}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate('/pricelist')} aria-label="Back to price list"><ArrowLeft size={16} /></button>
                    <div>
                        <h1 className={styles.pageTitle}>{isEditing ? 'Edit service' : 'Add services'}</h1>
                        <p className={styles.pageSubtitle}>{isEditing ? 'Change this service. Price and offer changes need "Change prices" access.' : 'Type rows or upload Excel / CSV. Men, Women and Kids can go together.'}</p>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    {isEditing && <span className={styles.modeLabel}>Editing 1 service</span>}
                    <button type="button" className={styles.cancelBtn} onClick={() => navigate('/pricelist')}><X size={13} /> Cancel</button>
                    <RequirePermission module="pricelist" action={isEditing ? 'edit' : 'create'}>
                        <button type="button" className={styles.saveBtn} onClick={save} disabled={!storeId || isSaving || !validRows.length}>
                            <Save size={13} /> {isSaving ? 'Saving...' : isEditing ? 'Save changes' : `Save ${validRows.length} services`}
                        </button>
                    </RequirePermission>
                </div>
            </header>

            {status.text && (
                <div className={`${styles.statusBar} ${status.type === 'error' ? styles.statusError : styles.statusSuccess}`} style={status.type === 'warn' ? { background: '#fff6e0', color: '#7a4a10' } : undefined}>
                    {status.text}
                    {status.details.length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{status.details.map((d) => <li key={d}>{d}</li>)}</ul>}
                </div>
            )}

            <main className={styles.workspace}>
                <section className={styles.gridPanel}>
                    <div className={styles.panelHeader}>
                        <strong>Review &amp; edit</strong>
                        {!isEditing && (
                            <RequirePermission module="pricelist" action="create">
                                <button type="button" className={styles.addRowBtn} onClick={addRow} disabled={!storeId}><Plus size={13} /> Add row</button>
                            </RequirePermission>
                        )}
                    </div>
                    <div className={styles.tableScroll}>
                        <table className={styles.serviceTable}>
                            <thead>
                                <tr>
                                    <th className={styles.numberCol}>#</th>
                                    <th className={styles.serviceNameCol}>Service name<span>*</span></th>
                                    <th className={styles.demographicCol}>Audience<span>*</span></th>
                                    <th className={styles.categoryCol}>Category<span>*</span></th>
                                    <th className={styles.durationCol}>Duration <small>(mins)</small><span>*</span></th>
                                    <th className={styles.priceCol}>Base price<span>*</span></th>
                                    <th className={styles.discountCol}>Offer</th>
                                    <th className={styles.effectiveCol}>Final price</th>
                                    <th className={styles.actionCol} />
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? <tr><td colSpan="9" className={styles.tableMessage}>Loading...</td></tr> : rows.map((row, index) => (
                                    <tr key={row.id}>
                                        <td className={styles.rowNumber}>{index + 1}</td>
                                        <td><input className={styles.cellInput} value={row.name} onChange={(e) => setField(row.id, 'name', e.target.value)} placeholder="Required" autoComplete="off" autoFocus={index === 0} /></td>
                                        <td><input className={styles.cellInput} list="service-audiences" value={row.demographic} onChange={(e) => setField(row.id, 'demographic', e.target.value)} placeholder="Men / Women / Kids" autoComplete="off" /></td>
                                        <td><input className={styles.cellInput} list="service-categories" value={row.category} onChange={(e) => setField(row.id, 'category', e.target.value)} placeholder="Category" autoComplete="off" /></td>
                                        <td><input className={`${styles.cellInput} ${styles.numericInput}`} type="number" min="1" value={row.duration} onChange={(e) => setField(row.id, 'duration', e.target.value)} placeholder="0" /></td>
                                        <td><div className={styles.currencyInput}><span>₹</span><input className={`${styles.cellInput} ${styles.numericInput}`} type="number" min="0" value={row.price} onChange={(e) => setField(row.id, 'price', e.target.value)} placeholder="0" /></div></td>
                                        <td className={styles.discountCell}>
                                            <div className={styles.discountEditor}>
                                                <select value={row.discountActive ? row.discountType : 'None'} onChange={(e) => setOffer(row.id, e.target.value)} aria-label="Offer type">
                                                    <option value="None">No offer</option><option value="Percentage">% off</option><option value="Flat">₹ off</option>
                                                </select>
                                                {row.discountActive && <input type="number" min="0" value={row.discountValue} onChange={(e) => setField(row.id, 'discountValue', e.target.value)} placeholder="0" aria-label="Offer value" />}
                                            </div>
                                        </td>
                                        <td className={styles.effectiveValue}>₹{effectivePrice(row).toFixed(2)}</td>
                                        <td className={styles.actionCell}>{!isEditing && <button type="button" className={styles.deleteBtn} onClick={() => removeRow(row.id)} title="Remove row"><Trash2 size={13} /></button>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.gridFooter}><span>{validRows.length} ready to save</span><span>Same name in another audience is a separate service</span></div>
                </section>

                <aside className={styles.uploadPanel}>
                    <h2>Excel / CSV upload</h2>
                    <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; importFile(f); }} hidden />
                    <RequirePermission module="pricelist" action="import" fallback={<div className={styles.warningBox}><AlertCircle size={15} /><p>Ask the owner for "Import price list" access to upload a file.</p></div>}>
                        <button type="button" className={styles.dropZone} onClick={() => fileInputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); importFile(e.dataTransfer.files?.[0]); }} disabled={isEditing || isImporting || !storeId}>
                            <Upload size={27} />
                            <strong>{isEditing ? 'Upload is for adding services' : isImporting ? 'Reading file...' : 'Upload Excel / CSV'}</strong>
                            <span>{isEditing ? 'Edit this service in the grid' : 'Drag and drop or click to choose'}</span>
                        </button>
                    </RequirePermission>
                    <button type="button" className={styles.sampleCard} onClick={template}>
                        <FileSpreadsheet size={16} />
                        <span><strong>Need the format?</strong><small>Download the sample</small></span>
                    </button>
                    <div className={styles.warningBox}>
                        <AlertCircle size={15} />
                        <p>{isEditing ? 'Only this service changes.' : 'A service already in the list (same Audience + Category + Name) is updated instead of added twice. For add-ons, use the Master editor.'}</p>
                    </div>
                    <RequirePermission module="pricelist" action="export">
                        <div className={styles.uploadBottom}>
                            <span>Download the grid</span>
                            <div>
                                <button type="button" onClick={() => writeCsv(exportRows(rows), 'Services.csv')} disabled={!validRows.length}>CSV</button>
                                <button type="button" onClick={() => writeSheet(exportRows(rows), 'Services.xlsx', 'Services')} disabled={!validRows.length}>Excel</button>
                            </div>
                        </div>
                    </RequirePermission>
                </aside>
            </main>

            <datalist id="service-audiences">{audiences.map((v) => <option key={v} value={v} />)}</datalist>
            <datalist id="service-categories">{categories.map((v) => <option key={v} value={v} />)}</datalist>
        </div>
    );
};

export default ServiceMaster;
